import React, { createContext, useContext, useState, useEffect } from 'react';
import { getBranchMode, getBranches } from '../api/branches';
import { useAuth } from './AuthContext';

// Roles that only ever work in their own branch (the server enforces the same)
const BRANCH_BOUND_ROLES = ['staff', 'branch_staff', 'branch_admin'];

/**
 * BranchContext - Global state for branch selection
 *
 * Provides:
 * - currentBranchId: Selected branch ID (null = all branches)
 * - branches: List of available branches
 * - branchMode: Mode info (singleBranchMode, branchCount)
 * - isMultiBranch: Quick check if in multi-branch mode
 * - selectBranch: Function to change branch selection
 * - getCurrentBranch: Get current branch object
 * - refreshBranches: Reload branch data
 */

const BranchContext = createContext(null);

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within BranchProvider');
  }
  return context;
};

export const BranchProvider = ({ children }) => {
  const { user } = useAuth();
  // Staff are pinned to their own branch - never to a branch someone else picked on this
  // computer (the saved choice is shared by everyone using the browser)
  const pinnedBranchId =
    user && BRANCH_BOUND_ROLES.includes(user.role) && user.current_branch_id
      ? Number(user.current_branch_id)
      : null;
  const [currentBranchId, setCurrentBranchId] = useState(() => {
    // Try to restore from localStorage
    const saved = localStorage.getItem('selectedBranchId');
    return saved ? parseInt(saved, 10) : null;
  });
  const [branches, setBranches] = useState([]);
  const [branchMode, setBranchMode] = useState({
    singleBranchMode: true,
    branchCount: 0,
    requiresBranchSelection: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip fetching on public pages or when not authenticated to avoid 401 errors
    const publicPaths = ['/login', '/forgot-password', '/reset-password', '/demo-login', '/sso', '/session-ended'];
    const isPublicPage = publicPaths.some((path) => window.location.pathname.startsWith(path));
    const hasAuthToken = !!localStorage.getItem('accessToken');

    // Signed in: on any page once the user is known (straight after login), else off the
    // public pages
    if (hasAuthToken && (user || !isPublicPage)) {
      fetchBranchData();
    } else {
      setLoading(false);
    }
    // Reload when a different user signs in
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (pinnedBranchId !== null && currentBranchId !== pinnedBranchId) {
      setCurrentBranchId(pinnedBranchId);
    }
  }, [pinnedBranchId, currentBranchId]);

  // Persist selection
  useEffect(() => {
    if (currentBranchId !== null) {
      localStorage.setItem('selectedBranchId', currentBranchId.toString());
    } else {
      localStorage.removeItem('selectedBranchId');
    }
  }, [currentBranchId]);

  const fetchBranchData = async () => {
    try {
      setLoading(true);
      const [modeRes, branchesRes] = await Promise.all([
        getBranchMode(),
        getBranches({ limit: 100, isActive: true })
      ]);

      setBranchMode(
        modeRes.data || {
          singleBranchMode: true,
          branchCount: 0,
          requiresBranchSelection: false
        }
      );
      setBranches(branchesRes.data || []);

      // If in single branch mode and we have one branch, auto-select it
      if (modeRes.data?.singleBranchMode && branchesRes.data?.length === 1) {
        setCurrentBranchId(branchesRes.data[0].id);
      }

      // If selected branch no longer exists, reset
      if (currentBranchId !== null) {
        const exists = branchesRes.data?.some((b) => b.id === currentBranchId);
        if (!exists) {
          setCurrentBranchId(null);
        }
      }
    } catch (error) {
      console.error('Error fetching branch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectBranch = (branchId) => {
    if (pinnedBranchId !== null) return; // staff stay in their own branch
    setCurrentBranchId(branchId);
  };

  const getCurrentBranch = () => {
    if (currentBranchId === null) return null;
    return branches.find((b) => b.id === currentBranchId) || null;
  };

  const refreshBranches = () => {
    return fetchBranchData();
  };

  const value = {
    currentBranchId: pinnedBranchId ?? currentBranchId,
    isBranchPinned: pinnedBranchId !== null,
    branches,
    branchMode,
    loading,
    isMultiBranch: !branchMode?.singleBranchMode,
    selectBranch,
    getCurrentBranch,
    refreshBranches
  };

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};

export default BranchContext;
